import { addSeries, applyEdit, canAddSeries, canInsertRow, canRemoveSeries, insertRow, removeRow, removeSeries, setCategory, setCell, setName, setText } from './chartEdit.js'
import { paragraphsText } from './chartTextEdit.js'
import { displayNumber, editableNumber, formatChartNumber, parseDateInput, parseNumberInput } from './numberFormat.js'

/**
 * RED-317 PR 3d-A — a chart's sheet as the data sheet (StudioDataSheet.vue)
 * shows it, and what the admin types turned into the override
 * (chartEdit.js). Pure, so it unit-tests in node.
 *
 * Columns: the category levels first (outermost left, a multi-level axis's
 * groups), then one per series. Rows: the categories. A text that does not
 * parse is not applied: it comes back in `invalid`, keyed by row and column,
 * and the sheet keeps showing it, flagged, until it is fixed (never cleared).
 */

const DAY_MS = 86400000
const EPOCH_1900 = Date.UTC(1899, 11, 30)
const SERIAL_1904 = 1462

export const invalidKey = (row, colKey) => `${row}|${colKey}`

/** The sheet's columns: [{ key, kind, leading, level?, idx?, header, headerEditable }]. */
export function sheetColumns(table, labels = {}) {
  const columns = []
  if (table.kind === 'number' || table.kind === 'date') {
    columns.push({ key: 'cat:0', kind: table.kind, leading: true, level: 0, header: labels.category ?? '', headerEditable: false, label: labels.category ?? '' })
  } else if (table.kind === 'text') {
    const depth = table.levels.length
    for (let level = depth - 1; level >= 0; level--) {
      const header = depth > 1 ? `${labels.category ?? ''} ${depth - level}`.trim() : (labels.category ?? '')
      columns.push({ key: `cat:${level}`, kind: 'text', leading: true, level, header, headerEditable: false, label: header })
    }
  }
  for (const series of table.series) {
    columns.push({ key: `s:${series.idx}`, kind: 'number', leading: false, idx: series.idx, header: series.name, headerEditable: true, label: series.name || labels.series || '' })
  }
  return columns
}

/** A date serial as the sheet edits it: d.m.yyyy. */
export function editableDate(value, date1904 = false) {
  if (value === null || value === undefined) return ''
  const date = new Date(EPOCH_1900 + Math.round(value + (date1904 ? SERIAL_1904 : 0)) * DAY_MS)
  return `${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`
}

function categoryCell(table, column, row, date1904) {
  if (column.kind === 'number' || column.kind === 'date') {
    const value = table.numbers?.[row] ?? null
    const display = value === null ? '' : formatChartNumber(value, table.display, date1904)
    return { display, edit: column.kind === 'date' ? editableDate(value, date1904) : editableNumber(value) }
  }
  const label = table.levels[column.level]?.[row] ?? null
  // An outer level's null continues the group above.
  const group = column.level > 0 ? (label === null && row > 0 ? 'cont' : 'start') : undefined
  return { display: label ?? '', edit: label ?? '', group }
}

/** The sheet's rows: [{ key, label, cells: [{ display, edit, invalid?, invalidMessage?, group? }] }]. */
export function sheetRows(table, columns, invalid = {}, messages = {}, date1904 = false) {
  const rows = []
  for (let row = 0; row < table.rows; row++) {
    const cells = columns.map((column) => {
      const cell = column.leading
        ? categoryCell(table, column, row, date1904)
        : (() => {
            const series = table.series.find((entry) => entry.idx === column.idx)
            const value = series?.values[row] ?? null
            return { display: displayNumber(value, series?.formatCode ?? 'General'), edit: editableNumber(value) }
          })()
      const typed = invalid[invalidKey(row, column.key)]
      if (typed !== undefined) {
        cell.invalid = typed
        cell.invalidMessage = column.kind === 'date' ? messages.date : messages.number
      }
      return cell
    })
    const first = cells.find((cell, index) => columns[index].leading && cell.display !== '')
    rows.push({ key: `r${row}`, label: first?.display ?? String(row + 1), cells })
  }
  return rows
}

/** A typed category or value: { ok, value } (the chartEdit value) or { ok: false }. */
export function parseCell(column, text, date1904 = false) {
  if (column.kind === 'text') return { ok: true, value: text }
  if (column.kind === 'date') {
    const parsed = parseDateInput(text)
    return parsed.ok && parsed.value !== null && date1904 ? { ok: true, value: parsed.value - SERIAL_1904 } : parsed
  }
  return parseNumberInput(text)
}

/**
 * Texts the admin committed → the next override and the invalid map.
 * Rows past the end are appended first (a pasted block may be longer than
 * the sheet). One call = one commit = one undo step. A block the chart
 * cannot hold (MAX_POINTS) is refused whole: nothing applied, `tooLarge`.
 *
 * @returns {{ edit, invalid, changed: boolean, tooLarge: boolean }}
 */
export function applyInputs(base, edit, inputs, invalid = {}, date1904 = false) {
  let next = edit
  const out = { ...invalid }
  const table = applyEdit(base, edit)
  const columns = sheetColumns(table)
  const lastRow = Math.max(-1, ...inputs.map((input) => input.row))
  if (lastRow >= table.rows && !canInsertRow(base, edit, lastRow + 1 - table.rows)) return { edit, invalid, changed: false, tooLarge: true }
  for (let row = table.rows; row <= lastRow; row++) next = insertRow(base, next, row)
  for (const { row, col, text } of inputs) {
    const column = columns[col]
    if (!column) continue
    const key = invalidKey(row, column.key)
    const parsed = parseCell(column, text, date1904)
    if (!parsed.ok) {
      out[key] = text
      continue
    }
    delete out[key]
    next = column.leading ? setCategory(base, next, row, column.level, parsed.value) : setCell(base, next, row, column.idx, parsed.value)
  }
  return { edit: next, invalid: out, changed: JSON.stringify(next) !== JSON.stringify(edit), tooLarge: false }
}

/** The invalid map after a row was inserted (delta 1) or removed (delta -1) at `at`. */
export function shiftInvalid(invalid, at, delta) {
  const out = {}
  for (const [key, text] of Object.entries(invalid)) {
    const [row, colKey] = [Number(key.slice(0, key.indexOf('|'))), key.slice(key.indexOf('|') + 1)]
    if (delta < 0 && row === at) continue
    out[invalidKey(row >= at + (delta < 0 ? 1 : 0) ? row + delta : row, colKey)] = text
  }
  return out
}

/** The invalid map without a removed series' column. */
export function dropInvalidColumn(invalid, colKey) {
  return Object.fromEntries(Object.entries(invalid).filter(([key]) => !key.endsWith(`|${colKey}`)))
}

/** The series idx the active column belongs to (a category column: the first series). */
export function seriesAt(table, column) {
  return column && !column.leading ? column.idx : (table.series[0]?.idx ?? null)
}

export { addSeries, canAddSeries, canInsertRow, canRemoveSeries, insertRow, removeRow, removeSeries, setName }

/** Whether an override changes the chart's data (what replaces a linked workbook), not only its texts. */
export function editsData(edit) {
  return Boolean(edit && (edit.rows || edit.series || edit.cells || edit.cats || edit.names))
}

// ------------------------------------------------------------- chart texts

/**
 * The texts on a chart the admin edits in place, by edit target
 * (data-target on the drawn text): titles, axis titles and text boxes from
 * the derived model, legend entries (series names) and text category labels.
 *   { [target]: { kind: 'text'|'name'|'cat', text, multiline, key?, idx?, level?, row? } }
 */
export function textTargets(served, derived, table) {
  const targets = {}
  const addText = (block, original) => {
    if (!block?.key || block.editable === false || original?.editable === false) return
    targets[block.key] = { kind: 'text', key: block.key, text: paragraphsText(block.paragraphs), baseText: paragraphsText(original?.paragraphs ?? block.paragraphs), multiline: true }
  }
  addText(derived.title, served.title)
  for (const [id, axis] of Object.entries(derived.axes ?? {})) addText(axis.title, served.axes?.[id]?.title)
  for (const shape of derived.shapes ?? []) addText(shape, (served.shapes ?? []).find((entry) => entry.key === shape.key))
  for (const series of table.series) targets[`name:${series.idx}`] = { kind: 'name', idx: series.idx, text: series.name, multiline: false }
  if (table.kind === 'text') {
    table.levels.forEach((level, depth) => {
      level.forEach((label, row) => {
        if (label !== null) targets[`cat:${depth}:${row}`] = { kind: 'cat', level: depth, row, text: label, multiline: false }
      })
    })
  }
  return targets
}

/** A text retyped on the chart → the next override. */
export function commitText(base, edit, target, text) {
  if (target.kind === 'name') return setName(base, edit, target.idx, text)
  if (target.kind === 'cat') return setCategory(base, edit, target.row, target.level, text)
  return setText(base, edit, target.key, text, target.baseText)
}
